import * as Sentry from '@sentry/react';
import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { whatsappApi } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import {
    MessageSquare,
    Send,
    Bot,
    User,
    Pause,
    Play,
    Circle,
    Loader2,
    Phone,
    Check,
    CheckCheck,
    AlertTriangle,
    Smile,
    Building2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

function MessageStatus({ status, sender }: { status?: string, sender: string }) {
    if (sender === 'user') return null;

    const colorClass = sender === 'ai' ? "text-primary-foreground/70" : "text-white/70";
    const readColorClass = sender === 'ai' ? "text-sky-300" : "text-sky-200";

    switch (status) {
        case 'sent':
            return <Check className={cn("h-3 w-3", colorClass)} />;
        case 'delivered':
            return <CheckCheck className={cn("h-3 w-3", colorClass)} />;
        case 'read':
            return <CheckCheck className={cn("h-3 w-3", readColorClass)} />;
        case 'failed':
        case 'undelivered':
            return <AlertTriangle className="h-3 w-3 text-destructive-foreground" />;
        default:
            return <Check className={cn("h-3 w-3 opacity-40", colorClass)} />;
    }
}

export default function WhatsAppPage() {
    const { user, gym, hasRole } = useAuth();
    const isSuperAdmin = hasRole(['super_admin']);
    const { toast } = useToast();
    const navigate = useNavigate();

    const [conversations, setConversations] = useState<any[]>([]);
    const [selectedConv, setSelectedConv] = useState<any>(null);
    const [messages, setMessages] = useState<any[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSelectedConv(null);
        setMessages([]);
        fetchConversations();
        const interval = setInterval(fetchConversations, 10000); // Poll every 10s
        return () => clearInterval(interval);
    }, [gym?.gym_id]);

    const fetchConversations = async () => {
        try {
            // Even if super admin, if we have a gym context, filter by it.
            // If they are in the "Global" view (no gym), they see all (if intended) or none.
            const targetGymId = gym?.gym_id;
            const res = await whatsappApi.listConversations(targetGymId);
            if (res.success) setConversations(res.conversations);
        } catch (error) {
            console.error('Error fetching conversations:', error);
            Sentry.captureException(error, { tags: { operation: 'whatsapp_fetch_conversations' } });
        }
    };

    useEffect(() => {
        if (!selectedConv) return;

        // Subscribe to changes for this conversation's messages
        const channel = supabase
            .channel(`whatsapp_messages_${selectedConv.id}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'whatsapp_messages',
                    filter: `conversation_id=eq.${selectedConv.id}`
                },
                (payload) => {
                    if (payload.eventType === 'INSERT') {
                        setMessages(prev => {
                            // Prevent duplicates if fetch or another source added it
                            if (prev.find(m => m.id === payload.new.id)) return prev;
                            return [...prev, payload.new];
                        });
                    } else if (payload.eventType === 'UPDATE') {
                        setMessages(prev => prev.map(m => m.id === payload.new.id ? payload.new : m));
                    } else if (payload.eventType === 'DELETE') {
                        setMessages(prev => prev.filter(m => m.id === payload.old.id));
                    }
                }
            )
            .subscribe();

        fetchMessages(selectedConv.id);

        return () => {
            supabase.removeChannel(channel);
        };
    }, [selectedConv?.id]);

    const fetchMessages = async (id: string) => {
        try {
            const res = await whatsappApi.getMessages(id);
            if (res.success) setMessages(res.messages);
        } catch (error) {
            console.error('Error fetching messages:', error);
            Sentry.captureException(error, { tags: { operation: 'whatsapp_fetch_messages' } });
        }
    };

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedConv || !newMessage.trim()) return;

        setIsSending(true);
        try {
            await whatsappApi.sendMessage(selectedConv.id, newMessage);
            setNewMessage('');
            fetchMessages(selectedConv.id);
            // Refresh conversation list to show ai_paused state
            fetchConversations();
        } catch (error: any) {
            Sentry.captureException(error, { tags: { operation: 'whatsapp_send_message' } });
            toast({
                title: 'Error al enviar',
                description: error.message,
                variant: 'destructive',
            });
        } finally {
            setIsSending(false);
        }
    };

    const toggleAI = async (paused: boolean) => {
        if (!selectedConv) return;
        try {
            await whatsappApi.toggleAI(selectedConv.id, paused);
            setSelectedConv((prev: any) => ({ ...prev, ai_paused: paused }));
            fetchConversations();
            toast({
                title: paused ? 'IA Pausada' : 'IA Reactivada',
                description: paused ? 'Ahora tienes el control manual.' : 'El asistente IA responderá automáticamente.',
            });
        } catch (error: any) {
            Sentry.captureException(error, { tags: { operation: 'whatsapp_toggle_ai' } });
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            });
        }
    };

    return (
        <DashboardLayout
            title="Asistente WhatsApp IA"
            subtitle="Monitorea y participa en conversaciones con clientes"
        >
            {!gym ? (
                <div className="flex-1 flex flex-col items-center justify-center p-12 text-center bg-muted/5 rounded-3xl border-2 border-dashed border-muted">
                    <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6 text-primary">
                        <Building2 className="h-10 w-10" />
                    </div>
                    <h2 className="text-2xl font-black text-foreground mb-2">Gimnasio no seleccionado</h2>
                    <p className="text-muted-foreground max-w-sm mb-8 font-medium">
                        Como Super Admin, debes seleccionar un gimnasio específico para poder gestionar sus conversaciones de WhatsApp.
                    </p>
                    <Button
                        size="lg"
                        className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 rounded-2xl"
                        onClick={() => navigate('/gyms')}
                    >
                        Ir a Gestión de Gimnasios
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-200px)]">

                    {/* Conversations List */}
                    <Card className="md:col-span-1 h-full flex flex-col overflow-hidden">
                        <CardHeader className="bg-primary/5 border-b py-4">
                            <CardTitle className="text-sm font-bold flex items-center gap-2">
                                <MessageSquare className="h-4 w-4" /> Chats Recientes
                            </CardTitle>
                        </CardHeader>
                        <ScrollArea className="flex-1">
                            <div className="divide-y divide-border">
                                {conversations.map((conv) => (
                                    <div
                                        key={conv.id}
                                        onClick={() => setSelectedConv(conv)}
                                        className={cn(
                                            "p-4 cursor-pointer transition-colors hover:bg-muted/50",
                                            selectedConv?.id === conv.id ? "bg-primary/5" : ""
                                        )}
                                    >
                                        <div className="flex justify-between items-start mb-1">
                                            <span className="font-bold text-sm">{conv.phone_number}</span>
                                            <span className="text-[10px] text-muted-foreground">
                                                {format(new Date(conv.last_message_at), 'HH:mm', { locale: es })}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {conv.ai_paused ? (
                                                <Badge variant="outline" className="text-[9px] bg-orange-500/10 text-orange-600 border-orange-200">
                                                    Atención Humana
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-[9px] bg-green-500/10 text-green-600 border-green-200">
                                                    IA Activa
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {conversations.length === 0 && (
                                    <div className="p-8 text-center text-muted-foreground text-xs italic">
                                        No hay conversaciones activas.
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    </Card>

                    {/* Chat Interface */}
                    <Card className="md:col-span-2 h-full flex flex-col overflow-hidden border-primary/20 bg-muted/5">
                        {selectedConv ? (
                            <>
                                <CardHeader className="bg-background border-b py-3 flex flex-row items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                            <Phone className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-sm font-bold">{selectedConv.phone_number}</CardTitle>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                <Circle className={cn("h-2 w-2 fill-current", selectedConv.ai_paused ? "text-orange-500" : "text-green-500")} />
                                                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                                                    {selectedConv.ai_paused ? 'Control Manual' : 'Asistente IA Respondiendo'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {selectedConv.ai_paused ? (
                                            <Button variant="outline" size="sm" className="h-8 gap-2 border-green-200 hover:bg-green-50 text-green-600" onClick={() => toggleAI(false)}>
                                                <Play className="h-3 w-3" /> Reactivar IA
                                            </Button>
                                        ) : (
                                            <Button variant="outline" size="sm" className="h-8 gap-2 border-orange-200 hover:bg-orange-50 text-orange-600" onClick={() => toggleAI(true)}>
                                                <Pause className="h-3 w-3" /> Pausar IA
                                            </Button>
                                        )}
                                    </div>
                                </CardHeader>

                                <ScrollArea className="flex-1 p-4 bg-muted/10">
                                    <div className="space-y-4">
                                        {messages.map((msg) => (
                                            <div
                                                key={msg.id}
                                                className={cn(
                                                    "flex flex-col max-w-[80%]",
                                                    msg.sender === 'user' ? "mr-auto" : "ml-auto items-end"
                                                )}
                                            >
                                                <div
                                                    className={cn(
                                                        "px-4 py-2 rounded-2xl text-sm shadow-sm relative",
                                                        msg.sender === 'user'
                                                            ? "bg-white text-slate-900 border rounded-tl-none"
                                                            : msg.sender === 'ai'
                                                                ? "bg-primary text-primary-foreground rounded-tr-none"
                                                                : "bg-orange-500 text-white rounded-tr-none"
                                                    )}
                                                >
                                                    {msg.content}
                                                    {msg.sender !== 'user' && (
                                                        <div className="flex justify-end mt-1 -mr-1">
                                                            <MessageStatus status={msg.status} sender={msg.sender} />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1.5 mt-1 px-1">
                                                    {msg.sender === 'ai' && <Bot className="h-3 w-3 text-muted-foreground" />}
                                                    {msg.sender === 'human' && <User className="h-3 w-3 text-muted-foreground" />}
                                                    <span className="text-[9px] text-muted-foreground">
                                                        {msg.sender === 'ai' ? 'Asistente IA' : msg.sender === 'human' ? `Manual (${msg.human_role})` : 'Cliente'} • {format(new Date(msg.created_at), 'HH:mm')}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                        <div ref={scrollRef} />
                                    </div>
                                </ScrollArea>

                                <div className="p-4 bg-background border-t">
                                    <form onSubmit={handleSendMessage} className="flex gap-2 items-center">
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-9 w-9 p-0 rounded-full hover:bg-muted"
                                                    disabled={!selectedConv.ai_paused || isSending}
                                                >
                                                    <Smile className="h-5 w-5 text-muted-foreground" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-64 p-2 mb-2" align="start">
                                                <div className="grid grid-cols-6 gap-1">
                                                    {['😀', '😂', '😍', '👍', '🙌', '💪', '🏋️‍♀️', '🏋️‍♂️', '🧘‍♀️', '🥗', '🥤', '🔥', '✨', '✅', '❌', '📅', '📱', '💬'].map((emoji) => (
                                                        <button
                                                            key={emoji}
                                                            type="button"
                                                            onClick={() => setNewMessage(prev => prev + emoji)}
                                                            className="h-8 w-8 flex items-center justify-center rounded hover:bg-muted text-lg transition-colors"
                                                        >
                                                            {emoji}
                                                        </button>
                                                    ))}
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                        <Input
                                            placeholder={selectedConv.ai_paused ? "Escribe un mensaje manual..." : "Pausa la IA para escribir un mensaje manual"}
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            disabled={!selectedConv.ai_paused || isSending}
                                            className="flex-1"
                                        />
                                        <Button type="submit" disabled={!selectedConv.ai_paused || !newMessage.trim() || isSending} size="icon" className="shrink-0 bg-primary hover:bg-primary/90">
                                            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                        </Button>
                                    </form>
                                    {!selectedConv.ai_paused && (
                                        <p className="text-[10px] text-center text-muted-foreground mt-2 italic">
                                            La IA tiene el control. Paúsala si necesitas intervenir.
                                        </p>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-4">
                                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                                    <MessageSquare className="h-8 w-8 opacity-20" />
                                </div>
                                <p className="text-sm font-medium">Selecciona una conversación para comenzar</p>
                            </div>
                        )}
                    </Card>
                </div>
            )}
        </DashboardLayout>
    );
}
